import { Flag02Icon } from "@hugeicons-pro/core-solid-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Link } from "react-router-dom";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import { escalationLabel } from "views/inbox/escalation";
import { formatCalendar } from "../helpers/format-calendar";
import { Notification } from "../types";

const summary = (reason?: string | null) =>
  reason === "frustration"
    ? "sounds frustrated with the replies so far"
    : "asked to speak with a person";

interface EscalatedConversationNotificationCardProps {
  item: Notification;
}

export const EscalatedConversationNotificationCard = ({
  item
}: EscalatedConversationNotificationCardProps) => {
  const queryClient = useQueryClient();
  const { id, read_at, url, metadata, created_at } = item;

  const { mutate: markAsRead } = useMutation({
    mutationKey: ["NOTIFICATIONS", "MARK_AS_READ"],
    mutationFn: (ids: number[]) =>
      instance.post("/portal/v1/notifications/mark_as_read", { ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["NOTIFICATIONS"] });
      queryClient.invalidateQueries({
        queryKey: ["UNREAD_NOTIFICATIONS_COUNT"]
      });
      queryClient.invalidateQueries({
        queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
      });
      queryClient.invalidateQueries({ queryKey: ["NEWEST_NOTIFICATIONS"] });
    }
  });

  const handleClick = () => {
    if (!read_at) markAsRead([id]);
  };

  const contactName = metadata.contact_name || "A prospect";

  return (
    <Link
      to={url}
      onClick={handleClick}
      className="p-6 bg-white hover:bg-alpha-40 cursor-pointer flex flex-col gap-3 w-full"
    >
      <div className="flex items-center justify-between gap-4">
        <span className="flex items-center gap-1.5 text-sm font-medium text-danger-500">
          <HugeiconsIcon icon={Flag02Icon} size={16} aria-hidden />
          {escalationLabel(metadata.reason)}
        </span>
        <p className="text-content-300 font-medium text-sm shrink-0">
          {formatCalendar(created_at)}
        </p>
      </div>
      <div className="flex items-center gap-4 min-w-0">
        <span
          className={cx(
            "size-2.5 rounded-full shrink-0",
            read_at ? "bg-alpha-40" : "bg-primary-500"
          )}
        />
        <Avatar name={contactName} size={44} className="font-bold" />
        <div className="min-w-0">
          <p className="text-lg font-bold text-content-1000 truncate">
            {contactName}
          </p>
          <p className="text-base text-content-400 truncate">
            {contactName} {summary(metadata.reason)}
          </p>
        </div>
      </div>
    </Link>
  );
};
